import { useEffect, useState } from "react";
import { AppMode, onboardingSteps } from "../values";
import { auth, firestore } from "../../firebase-config";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";

export function Onboarding({ appMode, updateOnboardingStatus, refreshOnboardingStatus }) {
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [userDocId, setUserDocId] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [initialLoading, setInitialLoading] = useState(true);
    const [animationDirection, setAnimationDirection] = useState('next');
    const [isAnimating, setIsAnimating] = useState(false);
    const [userData, setUserData] = useState({
        role: '',
        experience: '',
        interests: [],
    });

    useEffect(() => {
        const fetchUserDoc = async () => {
            if (!auth.currentUser) {
                history.push('/login');
                return;
            }
            try {
                const usersRef = collection(firestore, 'users');
                const q = query(usersRef, where('userId', '==', auth.currentUser.uid));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    setUserDocId(querySnapshot.docs[0].id);
                    const userData = querySnapshot.docs[0].data();
                    if (userData.role) setUserData(prev => ({ ...prev, role: userData.role }));
                    if (userData.experience) setUserData(prev => ({ ...prev, experience: userData.experience }));
                    if (userData.interests) setUserData(prev => ({ ...prev, interests: userData.interests }));
                }
            }
            catch (error) { }
            finally {
                setInitialLoading(false);
            }
        };
        fetchUserDoc();
    }, [history]);

    const handleNext = () => {
        const currentQuestion = onboardingSteps[currentStep];
        if (currentQuestion.type === 'select' && !userData[currentQuestion.id] && currentStep !== 0) {
            return;
        }
        if (currentQuestion.type === 'multiselect' && (!userData[currentQuestion.id] || userData[currentQuestion.id].length === 0) && currentStep !== 0) {
            return;
        }
        if (currentQuestion.type === 'textarea' && (!userData[currentQuestion.id] || userData[currentQuestion.id].trim() === '') && currentStep !== 0) {
            return;
        }

        if (currentStep < onboardingSteps.length - 1) {
            setAnimationDirection('next');
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
                setIsAnimating(false);
                window.scrollTo(0, 0);
            }, 300);
        } else {
            completeOnboarding();
        }
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            setAnimationDirection('prev');
            setIsAnimating(true);
            setTimeout(() => {
                setCurrentStep(currentStep - 1);
                setIsAnimating(false);
                window.scrollTo(0, 0);
            }, 300);
        }
    };

    const handleInputChange = (id, value) => {
        setUserData(prev => ({ ...prev, [id]: value }));
    };

    const handleMultiSelect = (id, value) => {
        setUserData(prev => {
            const currentValues = prev[id] || [];
            if (currentValues.includes(value)) {
                return { ...prev, [id]: currentValues.filter(item => item !== value) };
            } else {
                return { ...prev, [id]: [...currentValues, value] };
            }
        });
    };

    const completeOnboarding = async () => {
        if (!userDocId) {
            return;
        }

        setLoading(true);
        try {
            await updateDoc(doc(firestore, 'users', userDocId), {
                role: userData.role || '',
                experience: userData.experience || '',
                interests: userData.interests || [],
                onboardingCompleted: true,
                updatedAt: new Date().toISOString(),
            });

            if (updateOnboardingStatus) {
                updateOnboardingStatus(true);
            }

            if (refreshOnboardingStatus) {
                await refreshOnboardingStatus();
            }
            setTimeout(() => {
                history.push('/dashboard');
            }, 300);
        } catch (error) {
            setLoading(false);
        }
    };

    const isStepValid = () => {
        const step = onboardingSteps[currentStep];
        if (step.type === 'info') return true;
        if (step.type === 'select' && !userData[step.id]) return false;
        if (step.type === 'multiselect' && (!userData[step.id] || userData[step.id].length === 0)) return false;
        if (step.type === 'textarea' && (!userData[step.id] || userData[step.id].trim() === '')) return false;
        return true;
    };

    const renderStepContent = () => {
        const step = onboardingSteps[currentStep];
        switch (step.type) {
            case 'info':
                return (
                    <div className="text-center space-y-4">
                        <h2 className={`text-3xl font-bold ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{step.title}</h2>
                        <p className={`text-lg ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-300'}`}>{step.description}</p>
                    </div>
                );
            case 'select':
                return (
                    <div className="space-y-6">
                        <h2 className={`text-2xl font-semibold text-center ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{step.question}</h2>
                        <div className="grid gap-3 mt-4">
                            {step.options.map((option) => (
                                <button key={option} type="button" onClick={() => handleInputChange(step.id, option)} className={`p-4 rounded-lg text-left transition-all ${userData[step.id] === option ? appMode === AppMode.LIGHT ? 'bg-blue-50 border-2 border-blue-500 text-blue-700' : 'bg-blue-900/30 border-2 border-blue-400 text-blue-300' : appMode === AppMode.LIGHT ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50' : 'bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700'}`}>
                                    <div className="flex items-center">
                                        <div className={`w-5 h-5 rounded-full border flex-shrink-0 mr-3 flex items-center justify-center ${userData[step.id] === option ? appMode === AppMode.LIGHT ? 'border-blue-500 bg-blue-500' : 'border-blue-400 bg-blue-400'
                                            : appMode === AppMode.LIGHT
                                                ? 'border-gray-300'
                                                : 'border-gray-600'
                                            }`}>
                                            {userData[step.id] === option && <div className="w-2 h-2 rounded-full bg-white"></div>}
                                        </div>
                                        <span className="font-medium">{option}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 'multiselect':
                return (
                    <div className="space-y-6">
                        <h2 className={`text-2xl font-semibold text-center ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{step.question}</h2>
                        <p className={`text-sm text-center ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Select all that apply</p>
                        <div className="grid gap-3 mt-4">
                            {step.options.map((option) => (
                                <button key={option} type="button" onClick={() => handleMultiSelect(step.id, option)} className={`p-4 rounded-lg text-left transition-all ${userData[step.id]?.includes(option)
                                    ? appMode === AppMode.LIGHT
                                        ? 'bg-blue-50 border-2 border-blue-500 text-blue-700'
                                        : 'bg-blue-900/30 border-2 border-blue-400 text-blue-300'
                                    : appMode === AppMode.LIGHT
                                        ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                        : 'bg-gray-800 border border-gray-700 text-gray-200 hover:bg-gray-700'}`}>
                                    <div className="flex items-center">
                                        <div className={`w-5 h-5 rounded-md border flex-shrink-0 mr-3 flex items-center justify-center ${userData[step.id]?.includes(option)
                                            ? appMode === AppMode.LIGHT
                                                ? 'border-blue-500 bg-blue-500'
                                                : 'border-blue-400 bg-blue-400'
                                            : appMode === AppMode.LIGHT
                                                ? 'border-gray-300'
                                                : 'border-gray-600'}`}>
                                            {userData[step.id]?.includes(option) && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                        <span className="font-medium">{option}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 'textarea':
                return (
                    <div className="space-y-6">
                        <h2 className={`text-2xl font-semibold text-center ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{step.question}</h2>
                        <textarea value={userData[step.id] || ''} onChange={(e) => handleInputChange(step.id, e.target.value)} placeholder={step.placeholder} rows={5} className={`w-full p-4 rounded-lg border ${appMode === AppMode.LIGHT ? 'border-gray-200 focus:border-blue-500 text-gray-700 bg-white' : 'border-gray-700 focus:border-blue-400 text-white bg-gray-800'} focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300`}></textarea>
                    </div>
                );
            default:
                return null;
        }
    };

    if (initialLoading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gradient-to-br from-gray-50 to-gray-100' : 'bg-gradient-to-br from-gray-900 to-black'}`}>
                <div className="text-center">
                    <svg className="animate-spin h-10 w-10 mx-auto mb-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Loading your profile...</p>
                </div>
            </div>
        );
    }

    const getAnimationClass = () => {
        if (!isAnimating) return '';

        return animationDirection === 'next'
            ? 'animate-slide-out-left'
            : 'animate-slide-out-right';
    };

    return (
        <div className={`min-h-screen flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gradient-to-br from-gray-50 to-gray-100' : 'bg-gradient-to-br from-gray-900 to-black'}`}>
            <style jsx>{`
                @keyframes slideInRight {
                    from { transform: translateX(30px); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                
                @keyframes slideInLeft {
                    from { transform: translateX(-30px); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                
                @keyframes slideOutLeft {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(-30px); opacity: 0; }
                }
                
                @keyframes slideOutRight {
                    from { transform: translateX(0); opacity: 1; }
                    to { transform: translateX(30px); opacity: 0; }
                }
                
                .animate-slide-in-right {
                    animation: slideInRight 0.3s ease forwards;
                }
                
                .animate-slide-in-left {
                    animation: slideInLeft 0.3s ease forwards;
                }
                
                .animate-slide-out-left {
                    animation: slideOutLeft 0.3s ease forwards;
                }
                
                .animate-slide-out-right {
                    animation: slideOutRight 0.3s ease forwards;
                }
            `}</style>

            <div className="w-full max-w-xl px-6 py-12">
                <div className="mb-12">
                    <div className="flex justify-between items-center mb-2">
                        <span className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>Step {currentStep + 1} of {onboardingSteps.length}</span>
                        <span className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>{Math.round(((currentStep + 1) / onboardingSteps.length) * 100)}%</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${appMode === AppMode.LIGHT ? 'bg-gray-200' : 'bg-gray-700'}`}>
                        <div className={`h-2 rounded-full transition-all duration-500 ease-in-out ${appMode === AppMode.LIGHT ? 'bg-blue-600' : 'bg-blue-500'}`} style={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}></div>
                    </div>
                </div>
                <div className={`p-8 rounded-2xl ${appMode === AppMode.LIGHT ? 'bg-white shadow-xl border border-gray-100' : 'bg-gray-800/90 backdrop-blur-sm border border-gray-700 shadow-2xl'}`}>
                    <div className="min-h-[300px] flex items-center justify-center overflow-hidden">
                        <div className={`w-full ${getAnimationClass()} ${!isAnimating && animationDirection === 'next' ? 'animate-slide-in-right' : ''} ${!isAnimating && animationDirection === 'prev' ? 'animate-slide-in-left' : ''}`}>{renderStepContent()}</div>
                    </div>
                    <div className="mt-10 flex justify-between">
                        <button type="button" onClick={handlePrevious} disabled={currentStep === 0 || isAnimating} className={`px-6 py-3 rounded-lg font-medium ${currentStep === 0 || isAnimating
                            ? appMode === AppMode.LIGHT
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : appMode === AppMode.LIGHT
                                ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                                : 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600'
                            } transition-colors duration-300`}
                        > Back
                        </button>
                        <button type="button" onClick={currentStep === onboardingSteps.length - 1 ? completeOnboarding : handleNext} disabled={loading || isAnimating || (currentStep !== 0 && !isStepValid())} className={`px-6 py-3 rounded-lg font-medium text-white ${appMode === AppMode.LIGHT
                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                            : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 shadow-lg ${loading || isAnimating || (currentStep !== 0 && !isStepValid()) ? 'opacity-70 cursor-not-allowed' : ''
                            }`}>
                            {loading
                                ? <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg> Processing...
                                </span>
                                : currentStep === onboardingSteps.length - 1 ? 'Get Started' : 'Next'
                            }
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}